import {
	type ColorToken,
	cn,
	InfoIcon,
	XIcon,
} from "@au-schein-de/au-ui-library";
import { cva, type VariantProps } from "class-variance-authority";
import type { ReactNode } from "react";
import { useState } from "react";

const bannerVariants = cva("flex items-start gap-2 rounded-xl p-2", {
	variants: {
		variant: {
			warning: "bg-warning-100",
			info: "bg-info-100",
			error: "bg-error-100",
		},
	},
});

const variantIconColor: Record<string, ColorToken> = {
	warning: "warning-600",
	info: "info-600",
	error: "error-600",
};

interface DismissibleBannerProps extends VariantProps<typeof bannerVariants> {
	message: string;
	className?: string;
	icon?: ReactNode;
	testId?: string;
}

/**
 * A dismissible banner that supports multiple visual variants and custom icons.
 * Once dismissed, the banner is hidden until the component remounts.
 *
 * @param message - The text content to display in the banner
 * @param className - Optional classes merged onto the banner root
 * @param variant - Background color variant: "warning", "info", or "error". No background if omitted.
 * @param icon - Custom icon element. Falls back to InfoIcon colored by variant, or unstyled if no variant.
 * @param testId - Optional test ID for testing purposes (default: "dismissible-banner")
 *
 * @example
 * // With variant and custom icon
 * <DismissibleBanner
 *   message="This is how we calculate discounts..."
 *   variant="warning"
 *   icon={<InfoIcon size={4} colorToken="warning-400" />}
 *   testId="pdp-discount-info"
 * />
 *
 * @example
 * // With variant only — icon color matches variant
 * <DismissibleBanner
 *   message="Something went wrong"
 *   variant="error"
 * />
 *
 * @example
 * // No variant, no icon — plain banner with unstyled InfoIcon
 * <DismissibleBanner message="A neutral message" />
 */
export function DismissibleBanner({
	message,
	className,
	icon,
	variant,
	testId = "dismissible-banner",
}: DismissibleBannerProps) {
	const [isDismissed, setIsDismissed] = useState(false);
	const variantColorToken = variant ? variantIconColor[variant] : undefined;
	const resolvedIcon = icon ?? (
		<InfoIcon size={4} colorToken={variantColorToken} />
	);

	if (isDismissed) {
		return null;
	}

	return (
		<div
			className={cn(bannerVariants({ variant }), className)}
			data-testid={testId}
		>
			<div className="flex-shrink-0 mt-0.5">{resolvedIcon}</div>
			<p className="flex-1 text-body-sm text-gray-700">{message}</p>
			<button
				type="button"
				onClick={() => setIsDismissed(true)}
				className="flex-shrink-0 flex items-center justify-center w-10 h-10 p-2 cursor-pointer text-gray-700 hover:text-gray-500"
				data-testid={`${testId}-dismiss`}
				aria-label="Dismiss banner"
			>
				<XIcon.Raw className="w-5 h-5" />
			</button>
		</div>
	);
}
